import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import Link from "next/link";
import type React from "react";
import type { ComponentProps } from "react";
import { ConstructionConfirmedList } from "~/components/common/constructions/confirmed_list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DealEstimationHeaderStatus } from "~/components/common/deal_estimation_headers/DealEstimationHeaderStatus";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loading from "~/components/common/parts/loading/Loading";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useDealEstimationConfirmedHeaders } from "~/hooks/vendors/deals/useDealEstimationConfirmedHeaders";
import { useDealEstimationHeaders } from "~/hooks/vendors/deals/useDealEstimationHeaders";
import { useDeals } from "~/hooks/vendors/useDeals";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { getDownloadPath } from "~/utils/api/vendors/deals/DealEstimationHistoryConfirmedFile";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const id = useIdQueryHandler();
	const headerId = useIdQueryHandler("headerId");
	const { data: deal } = useDeals().show(id);
	const { data: dealEstimationHeaders } = useDealEstimationHeaders().index(id);
	const { data: dealEstimationHeaderContext } =
		useDealEstimationConfirmedHeaders().show(id, headerId);
	if (
		id === null ||
		headerId === null ||
		deal === null ||
		dealEstimationHeaders === null ||
		dealEstimationHeaderContext === null
	) {
		return <Loading isLoading />;
	}
	const dealEstimationHeader =
		dealEstimationHeaders.find((header) => header.id === headerId) ?? null;
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id, headerId);
		window.open(path);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り履歴確認" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	if (dealEstimationHeader === null) return null;
	return (
		<VendorLayoutV2>
			<VendorDealLayout deal={deal} items={items}>
				<Flex
					justifyContent="space-between"
					pt={isSp ? designTokens["spacing/m"] : undefined}
					px={isSp ? designTokens["spacing/m"] : undefined}
				>
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<h1 className={Styles.EstimateHeaderTitle}>見積り履歴確認</h1>
						<DealEstimationHeaderStatus
							dealEstimationHeader={dealEstimationHeader}
						/>
					</Flex>
					{!isSp ? (
						<Button
							disabled={
								dealEstimationHeaderContext.dealConstructions.length === 0
							}
							onClick={handleClickPreview}
							variant="secondary"
						>
							見積書ダウンロード
						</Button>
					) : null}
				</Flex>
				<ConstructionTop
					constructionContext={dealEstimationHeaderContext}
					readonly
				/>
				<ConstructionConfirmedList
					companyType="vendor"
					constructionContext={dealEstimationHeaderContext}
				/>
				<Box mt={designTokens["spacing/l"]}>
					<Panel>
						<PanelHeader title="備考" />
						<PanelBody>{dealEstimationHeader.note}</PanelBody>
					</Panel>
				</Box>
			</VendorDealLayout>
		</VendorLayoutV2>
	);
};
